import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../App';
import Layout from '../components/Layout';
import { Eye, Calendar, User, Edit, Trash, History, Tag, MessageCircle, Send, ExternalLink, Globe, Star, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const DocumentViewer = ({ user, setUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [showVersions, setShowVersions] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [workspace, setWorkspace] = useState(null);
  const [activeHeading, setActiveHeading] = useState('');

  // Extract headings for Table of Contents
  const tableOfContents = useMemo(() => {
    if (!document?.content) return [];
    
    const parser = new DOMParser();
    const doc = parser.parseFromString(document.content, 'text/html');
    const headings = doc.querySelectorAll('h1, h2, h3');
    
    return Array.from(headings).map((heading, index) => ({
      id: `heading-${index}`,
      text: heading.textContent,
      level: parseInt(heading.tagName.charAt(1)),
      originalTag: heading.tagName.toLowerCase()
    }));
  }, [document?.content]);

  // Add IDs to headings in content for navigation
  const contentWithIds = useMemo(() => {
    if (!document?.content) return '';
    
    let content = document.content;
    let headingIndex = 0;
    
    content = content.replace(/<(h[1-3])([^>]*)>/gi, (match, tag, attrs) => {
      const id = `heading-${headingIndex}`;
      headingIndex++;
      return `<${tag}${attrs} id="${id}">`;
    });
    
    return content;
  }, [document?.content]);

  // Scroll spy for active heading
  useEffect(() => {
    const handleScroll = () => {
      const headingElements = tableOfContents.map(h => window.document.getElementById(h.id)).filter(Boolean);
      
      for (let i = headingElements.length - 1; i >= 0; i--) {
        const element = headingElements[i];
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveHeading(tableOfContents[i].id);
            return;
          }
        }
      }
      
      if (tableOfContents.length > 0) {
        setActiveHeading(tableOfContents[0].id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents]);

  const scrollToHeading = (headingId) => {
    const element = window.document.getElementById(headingId);
    if (element) {
      const yOffset = -100;
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    fetchDocument();
    fetchVersions();
    fetchComments();
    fetchCategories();
  }, [id]);

  // Fetch workspace after document is loaded
  useEffect(() => {
    if (document?.workspace_id) {
      fetchWorkspaceByDocument();
    }
  }, [document?.workspace_id]);

  const fetchWorkspaceByDocument = async () => {
    try {
      // Get workspace list and find the one matching document's workspace_id
      const wsRes = await axios.get(`${API_URL}/auth/workspaces`);
      if (wsRes.data?.workspaces?.length > 0) {
        const docWorkspace = wsRes.data.workspaces.find(ws => ws.id === document.workspace_id);
        if (docWorkspace) {
          setWorkspace(docWorkspace);
        }
      }
    } catch (error) {
      console.error('Error fetching workspace:', error);
    }
  };

  const fetchDocument = async () => {
    try {
      const res = await axios.get(`${API_URL}/documents/${id}`);
      setDocument(res.data);
    } catch (error) {
      toast.error('Failed to fetch document');
    } finally {
      setLoading(false);
    }
  };

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/documents/${id}/versions`);
      setVersions(res.data);
    } catch (error) {
      console.error('Error fetching versions:', error);
    }
  };

  const fetchComments = async () => {
    try {
      const res = await axios.get(`${API_URL}/documents/${id}/comments`);
      setComments(res.data);
    } catch (error) {
      console.error('Error fetching comments:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/categories`);
      setCategories(res.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleCategoryChange = async (newCategory) => {
    try {
      await axios.put(`${API_URL}/documents/${id}`, {
        category: newCategory || null
      });
      toast.success('Category updated!');
      fetchDocument();
    } catch (error) {
      toast.error('Failed to update category');
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/documents/${id}`);
      toast.success('Document deleted successfully');
      navigate('/documents');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const toggleStar = async () => {
    try {
      const res = await axios.put(`${API_URL}/documents/${id}/star`);
      setDocument(prev => ({ ...prev, starred: res.data.starred }));
      toast.success(res.data.starred ? 'Document starred!' : 'Star removed');
    } catch (error) {
      toast.error('Failed to update star');
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setCommentLoading(true);
    try {
      await axios.post(`${API_URL}/documents/${id}/comments`, { content: newComment });
      setNewComment('');
      fetchComments();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setCommentLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-zinc-600">Loading document...</div>
        </div>
      </Layout>
    );
  }

  if (!document) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-screen">
          <div className="text-lg text-zinc-600">Document not found</div>
        </div>
      </Layout>
    );
  }

  const getVisibilityBadge = (visibility) => {
    const badges = {
      private: { color: 'bg-red-100 text-red-700', label: 'Private' },
      team: { color: 'bg-teal-100 text-teal-700', label: 'Team' },
      public: { color: 'bg-zinc-100 text-zinc-700', label: 'Public' }
    };
    return badges[visibility] || badges.team;
  };

  const visibilityBadge = getVisibilityBadge(document.visibility);

  return (
    <Layout user={user} setUser={setUser}>
      <div className="max-w-7xl mx-auto p-8" data-testid="document-viewer">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <h1 className="text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }} data-testid="document-title">
                  {document.title}
                </h1>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${visibilityBadge.color}`}>
                  {visibilityBadge.label}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-600">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{document.author_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{new Date(document.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="h-4 w-4" />
                  <span>{document.views} views</span>
                </div>
                <div className="px-3 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-medium">
                  v{document.version}
                </div>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Star Button */}
              <Button 
                variant="outline" 
                onClick={toggleStar}
                className={document.starred ? "bg-yellow-50 border-yellow-300 text-yellow-600" : ""}
                data-testid="star-document-button"
              >
                <Star className={`h-4 w-4 mr-2 ${document.starred ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                {document.starred ? 'Starred' : 'Star'}
              </Button>
              {/* Public Link Button */}
              {workspace && (document.visibility === 'public' || document.visibility === 'team') && (
                <a
                  href={`${window.location.origin}/portal/${workspace.subdomain}/document/${id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="text-teal-600 hover:bg-teal-50" data-testid="public-link-button">
                    <Globe className="h-4 w-4 mr-2" />
                    Public Link
                    <ExternalLink className="h-3 w-3 ml-1" />
                  </Button>
                </a>
              )}
              <Link to={`/documents/${id}/edit`}>
                <Button variant="outline" data-testid="edit-document-button">
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
              </Link>
              <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 hover:bg-red-50" data-testid="delete-document-button">
                    <Trash className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Document</DialogTitle>
                  </DialogHeader>
                  <p className="text-zinc-600">Are you sure you want to delete this document? This action cannot be undone.</p>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} data-testid="cancel-delete-button">
                      Cancel
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-button">
                      Delete
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                onClick={() => setShowVersions(!showVersions)}
                data-testid="show-versions-button"
              >
                <History className="h-4 w-4 mr-2" />
                History ({versions.length})
              </Button>
            </div>
          </div>

          {/* Meta Info */}
          <div className="flex flex-wrap gap-3 items-center">
            {document.category && (
              <span className="inline-flex items-center px-3 py-1 rounded-md bg-teal-100 text-teal-700 text-sm font-medium">
                {document.category}
              </span>
            )}
            {/* Quick Category Change */}
            <div className="flex items-center gap-2">
              <Label className="text-xs text-zinc-600">Change Category:</Label>
              <select
                value={document.category || ''}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="h-8 px-3 text-sm rounded-md border border-zinc-200 bg-white"
                data-testid="quick-category-select"
              >
                <option value="">No Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
            {document.tags && document.tags.map((tag, idx) => (
              <span key={idx} className="inline-flex items-center px-3 py-1 rounded-md bg-zinc-100 text-zinc-700 text-sm">
                <Tag className="h-3 w-3 mr-1" />
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Version History */}
        {showVersions && versions.length > 0 && (
          <div className="mb-8 bg-white border border-zinc-200 rounded-lg p-6" data-testid="version-history">
            <h2 className="text-xl font-bold text-zinc-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Version History
            </h2>
            <div className="space-y-3">
              {versions.map((version, idx) => (
                <div key={version.id} className={`p-4 border rounded-lg ${version.version_number === document.version ? 'border-violet-300 bg-violet-50' : 'border-zinc-200'}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${version.version_number === document.version ? 'bg-violet-600 text-white' : 'bg-zinc-100 text-zinc-700'}`}>
                          v{version.version_number}
                        </span>
                        <span className="text-sm text-zinc-600">
                          {new Date(version.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="h-4 w-4 text-zinc-400" />
                        <span className="font-medium text-zinc-700">
                          {version.created_by_name || 'Unknown user'}
                        </span>
                      </div>
                      {version.change_summary && (
                        <div className="mt-2 text-sm text-zinc-600 bg-zinc-50 p-2 rounded">
                          <span className="font-medium">Changes:</span> {version.change_summary}
                        </div>
                      )}
                      {!version.change_summary && idx < versions.length - 1 && (
                        <div className="mt-2 text-sm text-zinc-500 italic">
                          No change details recorded
                        </div>
                      )}
                      {idx === versions.length - 1 && (
                        <div className="mt-2 text-sm text-zinc-500 italic">
                          Initial version
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Table of Contents - Left Sidebar */}
          {tableOfContents.length > 0 && (
            <div className="hidden xl:block lg:col-span-2">
              <div className="bg-white border border-zinc-200 rounded-lg p-4 sticky top-8">
                <div className="flex items-center gap-2 mb-4">
                  <List className="h-4 w-4 text-violet-600" />
                  <h3 className="text-sm font-semibold text-zinc-900">Contents</h3>
                </div>
                <nav className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {tableOfContents.map((heading) => (
                    <button
                      key={heading.id}
                      onClick={() => scrollToHeading(heading.id)}
                      className={`block w-full text-left text-xs py-1.5 px-2 rounded transition-colors ${
                        activeHeading === heading.id
                          ? 'bg-violet-50 text-violet-700 font-medium'
                          : 'text-zinc-600 hover:bg-zinc-100'
                      }`}
                      style={{
                        paddingLeft: `${(heading.level - 1) * 8 + 8}px`,
                        borderLeft: activeHeading === heading.id ? '2px solid #7c3aed' : '2px solid transparent'
                      }}
                    >
                      <span className="line-clamp-2">{heading.text}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          )}

          {/* Document Content */}
          <div className={tableOfContents.length > 0 ? "lg:col-span-7 xl:col-span-7" : "lg:col-span-9"}>
            <div className="bg-white border border-zinc-200 rounded-lg p-8" data-testid="document-content">
              <div
                className="document-content prose prose-zinc prose-lg max-w-none"
                dangerouslySetInnerHTML={{ __html: contentWithIds }}
              />
            </div>
          </div>

          {/* Comments Sidebar */}
          <div className={tableOfContents.length > 0 ? "lg:col-span-5 xl:col-span-3" : "lg:col-span-3"}>
            <div className="bg-white border border-zinc-200 rounded-lg p-6 sticky top-8" data-testid="comments-section">
              <div className="flex items-center gap-2 mb-4">
                <MessageCircle className="h-5 w-5 text-violet-600" />
                <h3 className="text-lg font-bold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Comments ({comments.length})
                </h3>
              </div>

              {/* Add Comment */}
              <div className="mb-6">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  rows={3}
                  className="mb-2"
                  data-testid="comment-input"
                />
                <Button
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || commentLoading}
                  size="sm"
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  data-testid="add-comment-button"
                >
                  <Send className="h-4 w-4 mr-2" />
                  {commentLoading ? 'Adding...' : 'Add Comment'}
                </Button>
              </div>

              {/* Comments List */}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className="pb-4 border-b border-zinc-200 last:border-0" data-testid={`comment-${comment.id}`}>
                      <div className="flex items-start gap-3">
                        <div className="h-8 w-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                          <span className="text-violet-600 font-semibold text-sm">{comment.user_name.charAt(0)}</span>
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-sm text-zinc-900">{comment.user_name}</div>
                          <div className="text-xs text-zinc-500 mb-2">
                            {new Date(comment.created_at).toLocaleString()}
                          </div>
                          <p className="text-sm text-zinc-700">{comment.content}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-zinc-500">
                    <MessageCircle className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                    <p className="text-sm">No comments yet</p>
                    <p className="text-xs">Be the first to comment!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DocumentViewer;